/**
 * lib/collection-safety-patterns.ts — THE collection safety-filter pattern
 * lists (FRIDGE-153 finding-3 groundwork).
 *
 * These existed as two copies — lib/collection-page-data.ts and
 * app/api/collections/[slug]/route.ts — synchronized only by a "This MUST
 * match" comment. Verified identical 07/27/2026 (13 / 46 / 14 patterns,
 * comment-stripped extraction) and consolidated here; both former hosts now
 * import this module, and tests/collection-pattern-parity.test.ts fails if
 * either grows a local copy again.
 *
 * Every pattern is /i-flagged only — no /g anywhere, which is what makes
 * module-scope sharing safe (a shared /g regex is stateful across .test()
 * calls). If you ever add a /g pattern here, stop: it will corrupt matching
 * for every concurrent caller.
 *
 * The upcoming collection_eligible precompute encodes exactly these lists;
 * change them and the backfill must re-run (that build's parity-verify mode
 * will say so).
 */

export const EXCLUDE_PATTERNS = [
    // Non-fridge appliances
    /\b(washer|washing\s*machine|dryer|range|oven|cooktop|stove|microwave|dishwasher|dehumidifier|air\s*conditioner|air\s*purifier|purifier)\b/i,
    // FRIDGE-579 (owner, 08/27/2026): all-in-one kitchenette units — sink +
    // cooktop + fridge in one cabinet (Summit's "All-In-One Combo Kitchens"
    // line and peers). They CONTAIN a refrigerator but are not one, and they
    // were rendering on dorm/mini-fridge collections as if they were. Feed
    // titles often hide the sink ("...Freestanding Mini Fridge with Freezer"),
    // so the two title-blind rows in the C48EL family had their titles
    // corrected to Summit's own line name in the same change.
    /\b(kitchenette|combo\s*kitchens?|combination\s*kitchens?|compact\s*kitchens?|all[\s-]*in[\s-]*one\s*(combo\s*)?kitchens?)\b/i,
    // Non-powered cooling
    /\b(ice\s*chest|insulated\s*bag|thermal\s*bag|passive\s*cooling)\b/i,
    // Accessories/parts - more specific patterns to avoid false positives
    /\b(replacement\s*(filter|part|kit|gasket|seal|door|shelf|drawer|bulb|thermostat|control|board|sensor|cartridge|cartridges))\b/i,
    /\b(water\s*filter|air\s*filter|water\s*filtration|filtration\s*system|water\s*filter\s*pitcher)\b/i,
    /\b(filter\s*(basket|box|pad|pads|cartridge|cartridges|element|housing|media))\b/i,
    /\b(pond\s*filter|aquarium\s*filter|pool\s*filter|spa\s*filter)\b/i,
    /\b(juice\s*extractor|juicer|coffee\s*maker|kettle|toaster|blender|food\s*processor)\b/i,
    /\b(hepa)\b/i,
    /\b(wasserfilter|wasserfilterkrug|wasserfilterkanne)/i,
    /\b(fridge\s*(magnet|organizer|lock|thermometer))\b/i,
    /\b(refrigerator\s*(magnet|organizer|lock|thermometer|filter))\b/i,
    /\b(water\s*line|supply\s*line)\b/i,
    // Tools & installation
    /\b(dolly|installation\s*kit|strap|moving\s*tool)\b/i,
    // FRIDGE-277 — replacement parts and dispense hardware that arrive from
    // the feeds carrying an APPLIANCE type (a $128.79 cabinet hinge typed
    // `beverage-cooler`, a control unit typed `compact`). The row above only
    // catches the "replacement X" phrasing; these titles never say
    // "replacement", so they reached collections, carousels and price facets
    // as if they were appliances.
    //
    // Each pattern was counted against the WHOLE catalog (31,419 rows) before
    // it was added, and each matches ONLY genuine parts. The discipline that
    // makes that true: a part is the PRODUCT here, never a feature of one.
    // Appliance titles mention this vocabulary constantly — "... - Right
    // Hinge", "with Adjustable Shelves", "Drip Tray And Rail", "Draft Beer
    // Dispenser" — and none of them may match. That is why there is no bare
    // /hinge/, /shelf/ or /regulator/ pattern here: bare forms matched 444,
    // 260 and 22 rows respectively, almost all of them real appliances.
    /\btime[\s-]*delay[\s-]*relay\b/i,
    /\bgenuine[\s-]*oem[\s-]*part\b/i,
    /\bdrip[\s-]*pan\b/i,
    /\blavatory\b/i,
    /\bdraft[\s-]*tower\b/i,
    // Qualified regulators only. A bare /regulator/ would swallow "Watts
    // Regulator Company" — a brand name on real water filters.
    /\b(co2|nitrogen|beer|secondary)[\s-]*(draft[\s-]*beer[\s-]*)?regulator\b/i,
    // A hinge is the product only when the title names no appliance. With an
    // appliance noun present the hinge is a door-swing spec, not the item.
    // The noun list must stay EXHAUSTIVE: an appliance word missing from it
    // silently hides that appliance from every collection. Verified against
    // the whole catalog — the first draft of this pattern omitted "beverage
    // center", "wine vault" and "keg dispenser" and wrongly matched nine
    // real appliances.
    /^(?!.*\b(refrigerator|fridge|freezer|cooler|kegerator|ice[\s-]*maker|beverage[\s-]*cent(?:er|re)|wine[\s-]*(?:vault|cellar|cabinet)|(?:keg|beer)[\s-]*dispenser|chiller|wine[\s-]*fridge)\b).*\bhinge\b/i,
  ] as const;

export const NON_REFRIGERATION_FILTER_PATTERNS = [
    /\bcoffee[\s-]*(makers?|filters?|holders?|baskets?|machines?|brewers?|side)\b/i,
    /\b(makers?|filters?|holders?|baskets?|machines?|brewers?)[\s-]*(for[\s-]*)?coffee\b/i,
    /\bvacuum[\s-]*(cleaners?|filters?|bags?|robots?|sweepers?)\b/i,
    /\b(robot[\s-]?vacuum|robovac|roboterstaubsauger|staubsauger)\b/i,
    /\bpond[\s-]*(filters?|pumps?|kits?|fountains?|skimmers?)\b/i,
    /\bponds\b/i,
    /\baquarium[\s-]*(filters?|pumps?|tanks?)\b/i,
    /\b(swimming[\s-]*)?pool[\s-]*(filters?|cleaners?|skimmers?|cartridges?|pumps?)\b/i,
    /\binflatable[\s-]*pool\b/i,
    /\b(spa|hot[\s-]*tub|jacuzzi)[\s-]*(filters?|cleaners?|cartridges?|water)\b/i,
    /\bgarden[\s-]*hose\b/i,
    /\bshower[\s-]*(filters?|heads?)\b/i,
    /\b(humidifier|dehumidifier|hvac|furnace|hepa|air[\s-]*conditioner)[\s-]*filters?\b/i,
    /\bhepa[\s-]+filters?\b/i,
    /\bhepa\b/i,
    /\b(cat|pet|dog)[\s-]*(water|litter)[\s-]*(fountains?|filters?|bowls?)\b/i,
    /\bbio[\s-]?balls?\b/i,
    /\bsolar[\s-]*(fountains?|pumps?|ponds?)\b/i,
    /\b(faucet|tap|sink|under[\s-]?sink|countertop|whole[\s-]?house|reverse[\s-]?osmosis|ro)[\s-]*(water[\s-]*)?(filters?|filtration|systems?)\b/i,
    /\bfor[\s-]*(taps?|faucets?|sinks?)\b/i,
    /\bfits[\s-]*(standard[\s-]*)?(faucets?|taps?|sinks?)\b/i,
    /\bkitchen[\s-]*(faucet|sink|tap)\b/i,
    /\b(faucet|tap|sink)[\s-]*(combo|combination)\b/i,
    /\bjuice[\s-]*extractor|juicer\b/i,
    // Standalone water purification (not refrigerator-compatible) — pitchers,
    // jugs, dispensers, camping/portable purifiers.
    // FRIDGE-1498: not a refrigerator's own "AutoFill Pitcher" / "Auto Fill
    // Water Pitcher" (5 active French-door models, 10/16/2026: Cafe CGE29DP, GE
    // Profile PGE29BYTFS, GE PGD29BYTFS, Samsung RF30BB6200QL and
    // RF30BB6200QLAA), which this pattern dropped from every collection.
    /(?<!\bauto[\s-]?fill[\s-]+(?:water[\s-]+)?)\b(water[\s-]*)?(pitchers?|jugs?|kettles?)\b/i,
    /\bwater[\s-]*filter[\s-]*dispensers?\b/i,
    /\b(camping|survival|backpacking|outdoor)[\s-]*(water[\s-]*)?(filters?|purifiers?|purification)\b/i,
    /\bwater[\s-]*purifiers?\b/i,
    /\b(portable|electric|rechargeable)[\s-]*water[\s-]*filters?\b/i,
    // Coffee machines / espresso machines (incl. German Kaffeevollautomat,
    // and brand names that only sell coffee gear)
    // FRIDGE-1678: "espresso" drops an espresso machine or maker, or any title
    // that names no refrigerator, fridge or freezer (portafilter baskets,
    // espresso filters). It no longer drops a refrigerator: Forno names a
    // refrigerator line "Espresso" ("Milano Espresso 17.2 Cu. Ft. Bottom
    // Freezer Refrigerator"), and the bare word kept all 9 live ones off every
    // collection until 10/20/2026. Mirrored verbatim in lib/collection-membership.ts.
    /\b(kaffee|kaffeevollautomat|kaffeemaschine|espresso[\s-]*(?:machine|maker)s?)\b|^(?![\s\S]*\b(?:refrigerators?|fridges?|freezers?)\b)[\s\S]*\bespresso\b/i,
    /\b(jura|nivona|saeco|delonghi|de'?longhi|melitta|krups|bunn)\b/i,
    // Water bottles, RV/marine plumbing filters, and faucet-system brands —
    // none of these attach to a refrigerator.
    /\bwater[\s-]*bottles?\b/i,
    /\b(rv|marine|boat|camper)[\s-]*(\/[\s-]*\w+[\s-]*)?water[\s-]*filters?\b/i,
    /\bpur[\s-]*(systems?|filters?|faucets?)\b/i,
    /\bcompatible[\s-]*with[\s-]*pur\b/i,
    // German equivalents — "Krug"/"Kanne" = pitcher/jug, "Wasserhahn" = faucet,
    // "Theken" = countertop. Coffee covered by Kaffee* above.
    /\bwasserfilter[\s-]*(krug|kanne)\b/i,
    /\b(theken|counter)[\s-]*wasserfilter\b/i,
    /\bwasserhahn\b/i,
    /\bnon[\s-]?gravity[\s-]*water[\s-]*filters?\b/i,
    // Cuisinart never makes refrigerator water filters — any Cuisinart
    // product whose title is "X Filter Y" is a coffee/juice accessory.
    /\bcuisinart.{0,40}\b(filter[\s-]+(baskets?|covers?|holders?|panels?|assembly|bowls?|lids?|cups?))\b/i,
    /\bcuisinart.{0,40}\b(stainless[\s-]+steel[\s-]+filter|metal[\s-]+filter|full[\s-]+s\/s[\s-]+filter|permanent[\s-]+(nylon[\s-]+)?filter|nylon[\s-]+filter|scale[\s-]+filter|spout[\s-]+filter|tank[\s-]+filter|reusable[\s-]+filter|carafe[\s-]+lid[\s-]+filter|homebarista)\b/i,
    /\bcuisinart.{0,40}\b(filter[\s-]+holder|water[\s-]+filter[\s-]+holder|charcoal[\s-]+water[\s-]+filter[\s-]+holder)\b/i,
    /\bnyl(on)?[\s-]+filter[\s-]+basket\b/i,
    // Whole-house sediment / pre-filters — extend the earlier whole-house
    // rule to catch "Pre-Filter" suffix variants and sediment filters.
    /\bwhole[\s-]?house[\s-]?(pre[\s-]?)?(filter|filtration|system|sediment|pre.filter)/i,
    /\bspin[\s-]?down[\s-]+sediment[\s-]+filter\b/i,
    /\bsediment[\s-]+(pre[\s-]?)?filter\b/i,
  ] as const;

export const FILTER_ONLY_EXCLUDE_PATTERNS = [
    /\b(commercial[\s-]*)?ice[\s-]*(makers?|machines?)\b/i,
    /\bair[\s-]*purifiers?\b/i,
    /\bpurifiers?\b/i,
    /\bfoam[\s-]*(filters?|management|disks?|pads?)\b/i,
    /\b(sink|kitchen)[\s-]*extender\b/i,
    /\baerator\b/i,
    /\bfountain\b/i,
    /\b(faucet|tap)[\s-]*(extender|mount)\b/i,
    /\bfaucet[\s-]*and[\s-]*water[\s-]*filter\b/i,
    /\bcadr\b/i,
    /\b(berkey)\b/i,
    /\bjuice[\s-]*filter\b/i,
    /\b(animal|pet|cat|dog)[\s-]*water[\s-]*(dispenser|fountain|bowl)\b/i,
    /\bpool[\s-]*robot\b/i,
  ] as const;

// ---------------------------------------------------------------------------
// FRIDGE-651 Phase 3 (10/21/2026) — the water-coolers node and three patterns
// that are false positives there.
//
// Two of the eight live water coolers are caught by the sitewide lists above:
// Commercial Cool CCBL01, "Bottom Loading Water Dispenser, For 3 or 5 Gallon
// Water Bottle Jugs", by the pitcher/jug/kettle rule and the water-bottle
// rule; Commercial Cool CCDC02, "Reverse Osmosis Drinking Water Filter Cooler
// Dispenser", by the accessory rule's "water filter". Each rule is right
// sitewide: a jug, a bottle or a replacement filter is never an appliance.
// On a water cooler's own node they name the bottle the appliance holds and
// the filtration it does, not the product. The exemption is therefore the
// narrowest that covers the evidence: a row typed `water-cooler` (the
// classifier's no-refrigeration fence has already kept jugs, stands and
// replacement filters out of that type) on the water-coolers family of slugs
// (the node, its registered child, and their brand and colour composites, which
// end in the family slug; price and use-case variants reach every validator as
// the family slug). Every other pattern still runs for that row, and the same
// row keeps every other collection's verdict: on ice-makers CCBL01 is still a
// jug. tests/water-coolers-node-fridge-651.test.ts pins both directions.
//
// Each exempt pattern is the SAME object as the one in its list above, found by
// its source, so the parity test's counts and the one-copy rule are untouched;
// the membership mirror (lib/collection-membership.ts), which carries verbatim
// copies of the lists, matches by source for the same reason.
// ---------------------------------------------------------------------------

export const WATER_COOLER_NODE_SLUGS: ReadonlySet<string> = new Set(['water-coolers', 'hot-and-cold-water-coolers']);

/** The node, its child, or a brand/colour composite of either (`brio-water-coolers`). */
export function isWaterCoolerNodeSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  for (const family of WATER_COOLER_NODE_SLUGS) {
    if (slug === family || slug.endsWith(`-${family}`)) return true;
  }
  return false;
}

/** A row typed water-cooler being judged for the water-coolers family. */
export function isWaterCoolerNodeMember(type: string | null | undefined, slug: string | null | undefined): boolean {
  return type === 'water-cooler' && isWaterCoolerNodeSlug(slug);
}

function patternBySource(list: readonly RegExp[], fragment: string): RegExp {
  const found = list.find((re) => re.source.includes(fragment));
  if (!found) throw new Error(`collection-safety-patterns: no pattern with source fragment ${fragment}`);
  return found;
}

/** The three sitewide patterns a water cooler's own title trips on its own node. */
export const WATER_COOLER_NODE_FALSE_POSITIVES: ReadonlySet<RegExp> = new Set([
  patternBySource(NON_REFRIGERATION_FILTER_PATTERNS, 'pitchers?|jugs?|kettles?'),
  patternBySource(NON_REFRIGERATION_FILTER_PATTERNS, String.raw`\bwater[\s-]*bottles?\b`),
  patternBySource(EXCLUDE_PATTERNS, String.raw`water\s*filter|air\s*filter`),
]);

/** The same three, by source, for a host that holds its own verbatim copy of the lists. */
export const WATER_COOLER_NODE_FALSE_POSITIVE_SOURCES: ReadonlySet<string> = new Set(
  [...WATER_COOLER_NODE_FALSE_POSITIVES].map((re) => re.source),
);

/** Does any pattern in `list` block this lowercased title, skipping the three exempt ones when `exempt`? */
export function titleBlockedBy(list: readonly RegExp[], titleLower: string, exempt: boolean): boolean {
  for (const re of list) {
    if (exempt && WATER_COOLER_NODE_FALSE_POSITIVE_SOURCES.has(re.source)) continue;
    if (re.test(titleLower)) return true;
  }
  return false;
}

export function nonRefrigerationTitleBlocks(titleLower: string, exempt: boolean): boolean {
  return titleBlockedBy(NON_REFRIGERATION_FILTER_PATTERNS, titleLower, exempt);
}

export function excludePatternBlocks(titleLower: string, exempt: boolean): boolean {
  return titleBlockedBy(EXCLUDE_PATTERNS, titleLower, exempt);
}
